type Horizontal = 'left' | 'center' | 'right';
type Vertical = 'top' | 'center' | 'bottom';

const parse = (position: string): [number, number] => {
  const [horizontal, vertical = horizontal] = position.split(' ') as [
    Horizontal | Vertical,
    (Horizontal | Vertical)?,
  ];
  const factor = (value: string | undefined) =>
    value === 'left' || value === 'top'
      ? 0
      : value === 'right' || value === 'bottom'
        ? 1
        : 0.5;
  return [factor(horizontal), factor(vertical)];
};

/**
 * Places an overlay that is appended to the body by a popup position: the `my`
 * point of the overlay (e.g. 'right top') at the `at` point of the target, or
 * of the window without a target, shifted by `offset` and moved into the window
 * when it would stick out ('fit' collision).
 */
export const placeOverlay = (
  overlay: HTMLElement,
  target: Element | null,
  my: string,
  at: string,
  offset: [number, number] = [0, 0]
) => {
  const view = overlay.ownerDocument.defaultView || window;
  const bounds = target
    ? target.getBoundingClientRect()
    : { left: 0, top: 0, width: view.innerWidth, height: view.innerHeight };
  const [myX, myY] = parse(my);
  const [atX, atY] = parse(at);
  const width = overlay.offsetWidth;
  const height = overlay.offsetHeight;
  const fit = (value: number, size: number, available: number) =>
    Math.max(0, Math.min(value, available - size));
  const left = fit(
    bounds.left + bounds.width * atX - width * myX + offset[0],
    width,
    view.innerWidth
  );
  const top = fit(
    bounds.top + bounds.height * atY - height * myY + offset[1],
    height,
    view.innerHeight
  );
  // The coordinates are relative to the positioned parent: the page for an
  // overlay appended to the body, the viewport for one in the fixed CDK
  // overlay container.
  const parent = overlay.offsetParent;
  const origin =
    parent && parent !== overlay.ownerDocument.body
      ? parent.getBoundingClientRect()
      : { left: -view.scrollX, top: -view.scrollY };
  overlay.style.left = `${Math.round(left - origin.left)}px`;
  overlay.style.top = `${Math.round(top - origin.top)}px`;
};

/**
 * Places an overlay at its target: below the target and left aligned, above it
 * when there is no room below and right aligned when there is no room to the
 * right.
 */
export const placeAtTarget = (overlay: HTMLElement, target: Element | null) => {
  if (!target) {
    placeOverlay(overlay, null, 'center', 'center');
    return;
  }
  const view = overlay.ownerDocument.defaultView || window;
  const bounds = target.getBoundingClientRect();
  const below = bounds.bottom + overlay.offsetHeight <= view.innerHeight;
  const side =
    bounds.left + overlay.offsetWidth <= view.innerWidth ? 'left' : 'right';
  placeOverlay(
    overlay,
    target,
    `${side} ${below ? 'top' : 'bottom'}`,
    `${side} ${below ? 'bottom' : 'top'}`
  );
};

/**
 * The event to open an overlay panel with from code. The panel takes the
 * target from `currentTarget` and stops the propagation of the event, which
 * a plain `{ currentTarget }` object cannot do.
 */
export const openingEvent = (target: Element): Event => {
  const event = new Event('click');
  Object.defineProperty(event, 'currentTarget', { value: target });
  return event;
};
